import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag, Facebook, Instagram, Share2, MapPin, Star, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const GorillaTrekkingGuide = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Ultimate Guide to Gorilla Trekking | TravelCord Blog';
  }, []);

  const handleShare = async (platform: string) => {
    const url = window.location.href;
    const title = "Ultimate Guide to Gorilla Trekking in Uganda";

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'instagram':
        alert('Follow us on Instagram @travelcord.guide');
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (error) {
            console.error('Error sharing:', error);
          }
        }
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/LUalhrs.jpg"
            alt="Gorilla Trekking"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/blog" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="flex items-center mb-4 space-x-4">
              <img 
                src="https://imgur.com/ERlskFe.jpg"
                alt="David Mugisha"
                className="h-12 w-12 rounded-full border-2 border-white"
              />
              <div>
                <h3 className="text-lg font-medium">David Mugisha</h3>
                <p className="text-white/80 text-sm">Wildlife Expert</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="inline-flex items-center bg-ta-green/20 rounded-full px-2.5 py-0.5 text-xs text-ta-green mb-4 backdrop-blur-sm">
                Wildlife
              </div>
              
              <h1 className="text-4xl font-bold">Ultimate Guide to Gorilla Trekking in Uganda</h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-white/80">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  March 15, 2025
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  8 min read
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Bwindi Impenetrable Forest
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="prose max-w-none">
                <p className="lead text-lg text-gray-600">
                  Gorilla trekking in Uganda is one of Africa's most profound wildlife experiences. 
                  This comprehensive guide will help you plan your journey to meet these magnificent creatures 
                  in their natural habitat.
                </p>

                <h2>When to Visit</h2>
                <p>
                  The best time for gorilla trekking in Uganda is during the dry seasons: June to August 
                  and December to February. These months offer better hiking conditions with less rainfall 
                  and more stable trails. However, gorilla trekking is possible year-round, and visiting 
                  during the low season can mean better permit availability and lower accommodation rates.
                </p>

                <h2>Choosing Your Location</h2>
                <p>
                  Uganda offers gorilla trekking in two national parks:
                </p>
                <ul>
                  <li>
                    <strong>Bwindi Impenetrable National Park:</strong> Home to over half of the world's 
                    mountain gorilla population, with four different sectors for trekking.
                  </li>
                  <li>
                    <strong>Mgahinga Gorilla National Park:</strong> A smaller park that's part of the 
                    Virunga ecosystem, offering a more intimate experience.
                  </li>
                </ul>

                <h2>Permit Information</h2>
                <p>
                  Gorilla trekking permits in Uganda cost $700 per person. This includes:
                </p>
                <ul>
                  <li>One hour with a gorilla family</li>
                  <li>Professional guide services</li>
                  <li>Park entrance fees</li>
                  <li>Security escort</li>
                </ul>

                <div className="bg-gray-50 p-6 rounded-lg my-8">
                  <h3 className="text-lg font-semibold mb-4">Essential Tips</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Book permits 6-12 months in advance during peak season</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Wear appropriate hiking gear and bring rain protection</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Hire a porter to help with your equipment and the trek</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Stay hydrated and bring high-energy snacks</span>
                    </li>
                  </ul>
                </div>

                <h2>What to Pack</h2>
                <ul>
                  <li>Hiking boots with good ankle support</li>
                  <li>Waterproof jacket and pants</li>
                  <li>Long-sleeved shirts and trousers</li>
                  <li>Garden gloves for gripping vegetation</li>
                  <li>Camera (no flash photography allowed)</li>
                  <li>Water bottle and snacks</li>
                  <li>Insect repellent and sunscreen</li>
                </ul>

                <h2>The Trekking Experience</h2>
                <p>
                  Gorilla treks can last anywhere from 2-8 hours, depending on the gorillas' location. 
                  You'll be accompanied by experienced guides and trackers who will lead you through the 
                  forest. Once you reach the gorillas, you'll have one magical hour to observe them in 
                  their natural habitat.
                </p>

                <div className="my-8">
                  <img 
                    src="https://imgur.com/HE1ci9r.jpg" 
                    alt="Gorilla family in Bwindi"
                    className="rounded-lg w-full"
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    A gorilla family in Bwindi Impenetrable Forest
                  </p>
                </div>

                <h2>Conservation and Responsible Tourism</h2>
                <p>
                  Gorilla tourism plays a crucial role in conservation efforts. Your permit fee helps 
                  protect these endangered primates and supports local communities. Remember to follow 
                  all guidelines during your trek to ensure the gorillas' wellbeing and the sustainability 
                  of this incredible experience.
                </p>
              </div>

              {/* Tags */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-gray-600 mr-2">Tags:</span>
                  {["Gorilla Trekking", "Wildlife", "Adventure", "Uganda Travel", "Conservation"].map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-ta-green hover:text-white transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Share this article</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="h-5 w-5 mr-2" />
                    Facebook
                  </button>
                  <button
                    onClick={() => handleShare('instagram')}
                    className="flex items-center px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                  >
                    <Instagram className="h-5 w-5 mr-2" />
                    Instagram
                  </button>
                  <button
                    onClick={() => handleShare('native')}
                    className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <Share2 className="h-5 w-5 mr-2" />
                    Share
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Author Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <img 
                      src="https://imgur.com/ERlskFe.jpg"
                      alt="David Mugisha"
                      className="h-16 w-16 rounded-full"
                    />
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">David Mugisha</h3>
                      <p className="text-gray-600">Wildlife Expert</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    David has been leading gorilla treks for over 8 years and is passionate about 
                    wildlife conservation in Uganda.
                  </p>
                  <button className="w-full py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors">
                    Follow
                  </button>
                </div>

                {/* Table of Contents */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Table of Contents</h3>
                  <nav className="space-y-2">
                    <a href="#when-to-visit" className="block text-gray-600 hover:text-ta-green">When to Visit</a>
                    <a href="#choosing-location" className="block text-gray-600 hover:text-ta-green">Choosing Your Location</a>
                    <a href="#permit-information" className="block text-gray-600 hover:text-ta-green">Permit Information</a>
                    <a href="#what-to-pack" className="block text-gray-600 hover:text-ta-green">What to Pack</a>
                    <a href="#trekking-experience" className="block text-gray-600 hover:text-ta-green">The Trekking Experience</a>
                    <a href="#conservation" className="block text-gray-600 hover:text-ta-green">Conservation</a>
                  </nav>
                </div>

                {/* Related Articles */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    <Link to="/blog/best-time-visit-uganda" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/8u8WjGG.jpg"
                          alt="Best Time to Visit Uganda"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            Best Time to Visit Uganda
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">12 min read</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/blog/safari-photography-guide" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/Rl4EEiV.jpg"
                          alt="Safari Photography Guide"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            The Complete Safari Photography Guide
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">10 min read</p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default GorillaTrekkingGuide;